import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DocumentSnapshot } from 'firebase/firestore';
import { PostService } from '../services/postService';
import { Post } from '../types';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

interface ThanksPostsPanelProps {
  companyId: string;
}

export const ThanksPostsPanel: React.FC<ThanksPostsPanelProps> = ({ companyId }) => {
  const isDesktop = useIsDesktop();

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [expandedPosts, setExpandedPosts] = useState<Record<string, boolean>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollHint, setCanScrollHint] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const ITEMS_PER_PAGE = 10;

  const formatTimeAgo = useCallback((date: unknown) => {
    if (!date) return '';
    const now = new Date();
    const postDate = (date as { toDate?: () => Date }).toDate
      ? (date as { toDate: () => Date }).toDate()
      : new Date(date as string | number | Date);
    const diffInSeconds = Math.floor((now.getTime() - postDate.getTime())) / 1000;

    if (diffInSeconds < 60) return 'たった今';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}分前`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}時間前`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}日前`;
    return postDate.toLocaleDateString('ja-JP');
  }, []);

  const loadInitial = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await PostService.getApprovedPostsPaginated(
        ITEMS_PER_PAGE,
        undefined,
        'thanks',
        undefined,
        companyId
      );
      setPosts(result.posts);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (e: any) {
      console.error('ThanksPostsPanel: 投稿取得エラー:', e);
      setError('投稿の取得に失敗しました。');
      setPosts([]);
      setLastDoc(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadMore = useCallback(async () => {
    if (!companyId || !hasMore || loadingMore || loading) return;
    if (!lastDoc) return;
    setLoadingMore(true);
    setError(null);
    try {
      const result = await PostService.getApprovedPostsPaginated(
        ITEMS_PER_PAGE,
        lastDoc,
        'thanks',
        undefined,
        companyId
      );
      setPosts((prev) => [...prev, ...result.posts]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (e: any) {
      console.error('ThanksPostsPanel: 追加読み込みエラー:', e);
      setError('投稿の追加読み込みに失敗しました。');
    } finally {
      setLoadingMore(false);
    }
  }, [companyId, hasMore, lastDoc, loadingMore, loading]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const togglePostExpansion = useCallback((postId: string) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  }, []);

  const containerSx = useMemo(() => {
    if (isDesktop) {
      return {
        position: 'sticky' as const,
        top: 80,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto' as const,
        pr: 0.5,
      };
    }
    // モバイルはページ全体スクロールを増やしすぎないよう、パネル内スクロールに寄せる
    return {
      maxHeight: '42vh',
      overflowY: 'auto' as const,
    };
  }, [isDesktop]);

  // 「スクロールできます」ヒント表示条件を判定
  useEffect(() => {
    // 読み込みやエラー/空状態ではヒントを出さない
    if (loading || !!error || posts.length === 0) {
      setCanScrollHint(false);
      setHasUserScrolled(false);
      return;
    }

    // レンダリング後に高さを測る（初回はまだDOMが反映されていない可能性があるため）
    const rafId = window.requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) {
        setCanScrollHint(false);
        return;
      }
      const canScroll = el.scrollHeight > el.clientHeight + 1;
      setCanScrollHint(canScroll);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [loading, error, posts.length, isDesktop]);

  const handleScroll = useCallback(() => {
    if (hasUserScrolled) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    // 少しでもスクロールしたら以降はヒントを表示しない
    if (el.scrollTop > 0) {
      setHasUserScrolled(true);
    }
  }, [hasUserScrolled]);

  const handleHintClick = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    // アイコン押下でパネル内を下にスクロール（操作した時点でヒントは不要なので消す）
    setHasUserScrolled(true);
    el.scrollBy({
      top: Math.max(120, Math.floor(el.clientHeight * 0.75)),
      behavior: 'smooth',
    });
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: AppColors.textPrimary }}>
          みんなの感謝
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={loadInitial}
          disabled={loading || loadingMore}
          sx={{ color: AppColors.primary, fontWeight: 600 }}
        >
          更新
        </Button>
      </Box>

      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box ref={scrollContainerRef} onScroll={handleScroll} sx={containerSx}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Card sx={{ borderRadius: 3, boxShadow: 1, mb: 1.5 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 13, color: AppColors.error, mb: 1 }}>
                {error}
              </Typography>
              <Button variant="outlined" size="small" onClick={loadInitial}>
                再試行
              </Button>
            </CardContent>
          </Card>
        ) : posts.length === 0 ? (
          <Card sx={{ borderRadius: 3, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 13, color: AppColors.textSecondary, lineHeight: 1.6 }}>
                まだサンクス投稿がありません。あなたの「ありがとう」から始めましょう。
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {posts.map((post) => {
              const isExpanded = expandedPosts[post.id] || false;
              const content = post.processedContent || post.content;
              const needsExpansion = content.length > 120;

              return (
                <Card key={post.id} sx={{ borderRadius: 3, boxShadow: 1, mb: 1.25 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: AppColors.textPrimary,
                        whiteSpace: 'pre-line',
                        mb: needsExpansion ? 1 : 0,
                      }}
                      style={{
                        display: isDesktop ? 'block' : '-webkit-box',
                        WebkitLineClamp: isDesktop ? 'none' : (isExpanded ? 'none' : 3),
                        WebkitBoxOrient: 'vertical',
                        overflow: isDesktop ? 'visible' : 'hidden',
                      }}
                    >
                      {content}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 11, color: AppColors.textTertiary }}>
                        {formatTimeAgo(post.createdAt)}
                      </Typography>
                      {needsExpansion && !isDesktop && (
                        <Button
                          size="small"
                          onClick={() => togglePostExpansion(post.id)}
                          sx={{ p: 0, minWidth: 'auto', color: AppColors.primary, fontSize: 12 }}
                        >
                          {isExpanded ? '閉じる' : 'もっと見る'}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              {hasMore ? (
                <Button
                  variant="outlined"
                  onClick={loadMore}
                  disabled={loadingMore}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  {loadingMore ? <CircularProgress size={18} /> : 'さらに読み込む'}
                </Button>
              ) : (
                <Typography sx={{ fontSize: 12, color: AppColors.textTertiary }}>
                  これ以上の投稿はありません
                </Typography>
              )}
            </Box>
          </>
        )}
        </Box>

        {/* スクロール可能な場合にだけ、スクロール誘導アイコンを表示（スクロールしたら消す） */}
        {canScrollHint && !hasUserScrolled && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label="下にスクロール"
              onClick={handleHintClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHintClick();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                border: '1px solid #e0e0e0',
                boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                cursor: 'pointer',
                userSelect: 'none',
                '&:focus-visible': {
                  outline: `3px solid ${AppColors.primary}55`,
                  outlineOffset: 2,
                },
              }}
            >
              <KeyboardArrowDownIcon sx={{ color: AppColors.textSecondary }} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

